import { useContext, useEffect, useRef } from 'react';
import { useLocation, useRouteLoaderData } from 'react-router';
import { parseBasketJson, useShoppingBasket } from '../../common/hooks/useShoppingBasket.js';
import { isInBrowser } from '../../common/utils/ssrUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { SiteContext } from '../../public/site/SiteContext.js';
import { Modal } from '../Modal/Modal.js';
import { ChatFeedbackSurvey } from './ChatFeedbackSurvey.js';
import {
  SURVEY_SUCCESS_DISPLAY_DURATION_MS,
  WIDGET_VISIBILITY_MAX_ATTEMPTS,
  WIDGET_VISIBILITY_POLL_MS,
} from './constants.js';
import { getElisaChatClient, useGenesysChat, useGenesysSessionDetection, useSurveyOverlay } from './hooks/index.js';
import { detectDeviceType, detectService, trimOversizedAttributes } from './utils.js';
import type { GenesysCustomAttributes } from './types.js';
import type { ShoppingBasketType } from '../../common/types/shoppingBasket.js';
import type { PageResponse } from '../../generated/api/pageResponse.js';

import './GenesysChat.scss';

export interface GenesysChatProps {
  deploymentId: string;
  serviceName?: string;
  widgetCdnUrl?: string;
}

const GenesysChatInternal = ({ deploymentId, serviceName, widgetCdnUrl }: GenesysChatProps) => {
  const { authenticatedUser, isLoading } = useAuth();
  const location = useLocation();
  const pageData = useRouteLoaderData('public-path-root') as PageResponse | undefined;
  const { siteLanguage } = useContext(SiteContext);
  const { shoppingBasket } = useShoppingBasket();

  const detectedService = detectService(location.pathname, pageData?.siteStructureNodes);

  useGenesysSessionDetection(true);

  const {
    isSurveyPending,
    isBotOnlySurvey,
    onSurveySubmit,
    onSurveyClose,
    updateCustomAttributes,
    getSurveyConversationId,
    surveyAgentName,
  } = useGenesysChat({
    deploymentId,
    serviceName: serviceName || detectedService,
    enabled: !isLoading,
    widgetCdnUrl,
  });

  const { isOverlayVisible, openOverlay, closeOverlay } = useSurveyOverlay();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const attributes: Partial<GenesysCustomAttributes> = {
      chatStartUrl: window.location.href.split('?')[0],
      service: detectService(location.pathname, pageData?.siteStructureNodes),
      deviceType: detectDeviceType(),
    };

    if (authenticatedUser) {
      attributes.individualId = authenticatedUser.businessId;
      attributes.firstName = authenticatedUser.firstName;
      attributes.lastName = authenticatedUser.lastName;
      attributes.email = authenticatedUser.email;
      if (isInBrowser()) {
        attributes.chatToken = authenticatedUser.genesysChatToken;
      }
    }

    if (siteLanguage) {
      attributes.userLanguage = siteLanguage;
    }

    if (shoppingBasket) {
      const shoppingBasketJson: ShoppingBasketType | undefined = parseBasketJson(shoppingBasket);
      const shoppingBasketCpGuids =
        shoppingBasketJson?.items?.map(item => ({ guid: item.offer.commercialProductGuid, amount: item.quantity })) ||
        [];
      attributes.shoppingCart = shoppingBasketCpGuids.length > 0 ? JSON.stringify(shoppingBasketCpGuids) : undefined;
    }

    updateCustomAttributes(trimOversizedAttributes(attributes));
  }, [
    isLoading,
    authenticatedUser,
    shoppingBasket,
    siteLanguage,
    location.pathname,
    pageData?.siteStructureNodes,
    updateCustomAttributes,
  ]);

  const successTimerRef = useRef<number>(undefined);

  useEffect(() => {
    if (widgetCdnUrl) {
      document.body.classList.remove('genesys-chat-hidden');

      let timeoutId: number | undefined;
      let attempts = 0;

      const applyVisibility = () => {
        const client = getElisaChatClient();
        if (client) {
          client.visible(true);
          return;
        }

        attempts += 1;
        if (attempts < WIDGET_VISIBILITY_MAX_ATTEMPTS) {
          timeoutId = window.setTimeout(applyVisibility, WIDGET_VISIBILITY_POLL_MS);
        }
      };

      applyVisibility();

      return () => {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
      };
    }

    document.body.classList.remove('genesys-chat-hidden');

    return () => {
      document.body.classList.remove('genesys-chat-hidden');
    };
  }, [widgetCdnUrl]);

  // Cleanup success timer on unmount
  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    closeOverlay();
    onSurveyClose();
  };

  useEffect(() => {
    if (isSurveyPending && !isOverlayVisible) {
      openOverlay();
    }
  }, [isSurveyPending, isOverlayVisible, openOverlay]);

  return (
    <>
      {isOverlayVisible && (
        <Modal closeable onClose={handleClose} size="s" className="chat-survey-modal">
          <ChatFeedbackSurvey
            onSubmit={async data => {
              onSurveySubmit(data);
              successTimerRef.current = window.setTimeout(handleClose, SURVEY_SUCCESS_DISPLAY_DURATION_MS);
            }}
            onClose={handleClose}
            surveyMessageId={getSurveyConversationId() || undefined}
            isBotOnly={isBotOnlySurvey}
            agentName={surveyAgentName}
          />
        </Modal>
      )}
    </>
  );
};

export const GenesysChat = (props: GenesysChatProps) => {
  if (!isInBrowser()) {
    return null;
  }

  return <GenesysChatInternal {...props} />;
};
